/**
 * IAA draft export client (#50 stage 2).
 *
 * Unlike the JSON api wrapper, the export response is a binary .docx —
 * we use a raw same-origin fetch (credentials included, like client.ts)
 * and hand back a Blob + the server-decided file name. Errors still
 * arrive as JSON; the structured 409 ("iaa_export_blocked") carries the
 * per-person missing-field lists the modal renders.
 */
import type { DocumentLanguage, ISODate, UUID } from './types';

export interface IaaExportPartyPick {
  party_id: UUID;
  /** Empresa holders only: representative's cargo (free text). */
  position?: string | null;
}

export interface IaaExportRequestBody {
  language: DocumentLanguage;
  document_date: ISODate;
  parties: IaaExportPartyPick[];
}

export interface IaaExportMissingRecord {
  person_id: UUID | null;   // null = the Agreement itself
  person_name: string;
  fields: string[];
}

export class IaaExportBlockedError extends Error {
  missing: IaaExportMissingRecord[];
  constructor(message: string, missing: IaaExportMissingRecord[]) {
    super(message);
    this.missing = missing;
  }
}

export interface IaaExportResult {
  blob: Blob;
  fileName: string;
  exportId: string | null;
}

export async function exportIaaDraft(
  agreementId: UUID,
  body: IaaExportRequestBody,
): Promise<IaaExportResult> {
  const res = await fetch(`/agreements/${agreementId}/iaa-exports`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = (await res.json()).detail; } catch { /* non-JSON error body */ }
    if (
      res.status === 409 && detail && typeof detail === 'object'
      && (detail as { code?: string }).code === 'iaa_export_blocked'
    ) {
      const d = detail as { message: string; missing: IaaExportMissingRecord[] };
      throw new IaaExportBlockedError(d.message, d.missing);
    }
    const msg = typeof detail === 'string'
      ? detail
      : 'The draft could not be generated. Please try again.';
    throw new Error(msg);
  }
  // Server names the file via Content-Disposition (RFC 5987 filename*).
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const fileName = m ? decodeURIComponent(m[1]) : 'IAA_DRAFT.docx';
  return {
    blob: await res.blob(),
    fileName,
    exportId: res.headers.get('X-Iaa-Export-Id'),
  };
}

/** Trigger a browser download of the returned blob. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // DEFER the revoke: revoking synchronously after click() races the browser's
  // asynchronous download start and can silently drop the download (the "success
  // toast fired but nothing downloaded" flakiness — 2026-07-16 prod smoke). A delayed
  // revoke lets the download begin; the URL is freed shortly after (and on unload anyway).
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
